import { useContext } from "react";
import {
  ScopesContext,
  type ScopeNode,
  type ScopeType,
  type SelectedScope,
} from "./ScopeProvider/ScopeProvider";
import { ScopeManagementContext } from "../../Findings/FindingsFilters/ScopeHierarchy/ScopeManagement/ScopeManagementContext";
import { LS_SCOPE } from "../../Findings/FindingsFilters/FilterUtils";
import { resourceTypes } from "../../Findings/FindingsUtils";

export interface ScopeSelectionNode {
  id?: string;
  name?: string;
  type?: ScopeType;
  editable?: boolean;
  children?: SelectedScope[];
  level?: number;
  parentId?: string | null;
  /** Raw `filter_data`, serialized into `value.query` for a scope node. */
  filterData?: unknown;
}

/**
 * Resolves `scopeId` for callers that only hold an id (e.g. dashboard widgets).
 * Unlike `ScopesContext.searchTree` it tracks the walk, so it can supply `level`
 * (depth, root at 0 — matching the `Tree`) and `parentId`, which the raw
 * `ScopeNode` lacks. `level` is load-bearing: without it `LocationBreadcrumbs`
 * takes its root-only branch, dropping the selected scope's type icon.
 */
export const findScopeSelectionNode = (
  root: ScopeNode | undefined,
  scopeId: string | undefined
): ScopeSelectionNode | null => {
  if (!root || !scopeId) {
    return null;
  }

  const walk = (
    node: ScopeNode,
    level: number,
    parentId: string | null
  ): ScopeSelectionNode | null => {
    // Mirrors `searchTree`'s comparison, which coerces the node id.
    if (node?.id?.toString() === scopeId) {
      return {
        id: node.id,
        name: node.name,
        type: node.type,
        editable: node.editable,
        children: node.children as unknown as SelectedScope[] | undefined,
        level,
        parentId,
        filterData: node.filter_data,
      };
    }

    for (const child of node?.children ?? []) {
      const found = walk(child, level + 1, node.id ?? null);
      if (found) {
        return found;
      }
    }

    return null;
  };

  return walk(root, 0, null);
};

export interface UseSelectScopeOptions {
  /**
   * Overrides the `ScopeManagementContext` `setDefaultsForScopeAndFilter` gate,
   * for callers outside that provider which would otherwise never persist.
   */
  persistSelection?: boolean;
}

export interface UseSelectScopeReturnValue {
  selectScope: (node: ScopeSelectionNode) => void;
  isScopeSelected: (scopeId?: string) => boolean;
}

/**
 * Applies a scope or group as the page-level scope — one implementation shared by
 * the scope hierarchy tree (`ScopesList`) and the Scopes Overview widget.
 */
const useSelectScope = ({
  persistSelection,
}: UseSelectScopeOptions = {}): UseSelectScopeReturnValue => {
  const {
    scopesState: { selectedScope },
    updateScopeState,
  } = useContext(ScopesContext);
  const { setDefaultsForScopeAndFilter } = useContext(ScopeManagementContext);

  const shouldPersist = persistSelection ?? setDefaultsForScopeAndFilter;

  const isScopeSelected = (scopeId?: string): boolean =>
    selectedScope?.value?.id === scopeId;

  const selectScope = (node: ScopeSelectionNode): void => {
    if (isScopeSelected(node.id)) {
      return;
    }

    const newSelectedScope: SelectedScope = {
      label: node.name,
      value: {
        id: node.id,
        editable: node.editable,
        type: node.type,
        children: node.children,
        isDirty: false,
      },
      level: node.level,
      parentId: node.parentId,
    };

    let scopeToExecute = "";
    if (node.type === resourceTypes.SCOPE) {
      newSelectedScope.value.query = JSON.stringify(node.filterData);
      scopeToExecute = newSelectedScope.value.query;
    } else if (node.type === resourceTypes.GROUP) {
      newSelectedScope.value.query = "";
    }

    updateScopeState({
      selectedScope: newSelectedScope,
      scopeToExecute: scopeToExecute,
      isNewScope: false,
      isDirty: false,
    });

    // Persisted after the state write, and guarded: `setItem` throws in Safari
    // private mode and on quota exceeded, so doing it first would lose the
    // selection entirely. This way a failure only costs persistence.
    if (shouldPersist) {
      try {
        localStorage.setItem(LS_SCOPE, JSON.stringify(newSelectedScope));
      } catch {
        // Not remembered across reloads.
      }
    }
  };

  return { selectScope, isScopeSelected };
};

export default useSelectScope;
